/**
 * One row of quick actions with horizontal scroll.
 * Wrapper with -mx is in HomePage; this component is the scrollable flex.
 * Scroll position is preserved in sessionStorage when navigating back.
 */

import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { QUICK_ACTIONS, type QuickActionIcon } from "../../config/home.ts";
import { haptic } from "../../lib/telegram.ts";
import {
  IconAcademicCap,
  IconBanknotes,
  IconBriefcase,
  IconGift,
  IconHeartHands,
  IconCalendar,
  IconDocument,
  IconDove,
  IconFamily,
  IconHeartFilled,
  IconFoldedHands,
  IconHomeHeart,
  IconMapPin,
  IconMessage,
  IconReceipt,
  IconUser,
  IconUsers,
  IconEnvelope,
  IconSparkle,
  IconCrown,
  IconYouth,
} from "../../components/Icons.tsx";

const ICON_MAP: Record<
  QuickActionIcon,
  React.ComponentType<{ className?: string }>
> = {
  academicCap: IconAcademicCap,
  heart: IconHeartFilled,
  hearts: IconHeartFilled,
  foldedHands: IconFoldedHands,
  receipt: IconReceipt,
  mapPin: IconMapPin,
  message: IconMessage,
  document: IconDocument,
  calendar: IconCalendar,
  user: IconUser,
  users: IconUsers,
  dove: IconDove,
  envelope: IconEnvelope,
  homeHeart: IconHomeHeart,
  sparkle: IconSparkle,
  briefcase: IconBriefcase,
  banknotes: IconBanknotes,
  gift: IconGift,
  heartHands: IconHeartHands,
  crown: IconCrown,
  family: IconFamily,
  youth: IconYouth,
};

const ICON_COLORS: Record<string, string> = {
  church: "bg-sky-50/80 text-sky-700/75",
  worship: "bg-violet-50/80 text-violet-700/75",
  homeGroups: "bg-rose-50/80 text-rose-600/75",
  prayers: "bg-amber-50/80 text-amber-700/75",
  clubs: "bg-teal-50/80 text-teal-700/75",
  donations: "bg-emerald-50/80 text-emerald-700/75",
  leadershipSchool: "bg-indigo-50/80 text-indigo-700/75",
  womensBreakfasts: "bg-pink-50/80 text-pink-700/75",
  masterClasses: "bg-purple-50/80 text-purple-700/75",
  familyClub: "bg-rose-50/80 text-rose-600/75",
  youth: "bg-cyan-50/80 text-cyan-700/75",
  startCourse: "bg-amber-50/80 text-amber-700/75",
};

function QuickActionCell({ action }: { action: (typeof QUICK_ACTIONS)[0] }) {
  const Icon = ICON_MAP[action.icon];
  const colorClasses = ICON_COLORS[action.id] ?? "bg-slate-100 text-slate-600";

  return (
    <Link
      to={action.path}
      className="flex flex-col items-center justify-center gap-1 py-3 px-3 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit w-[120px] min-w-[120px] flex-shrink-0 snap-start lg:w-auto lg:min-w-0"
      aria-label={action.label}
      onClick={() => haptic("selection")}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${colorClasses}`}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-xs font-medium text-slate-700 text-center leading-tight">
        {action.label}
      </span>
    </Link>
  );
}

const SCROLL_STORAGE_KEY = "quickActionsGridScroll";

export function QuickActionsGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (saved) {
      const scrollLeft = parseInt(saved, 10);
      if (!Number.isNaN(scrollLeft)) {
        // Restore after layout (scroll width may not be ready on mount)
        const raf = requestAnimationFrame(() => {
          el.scrollLeft = scrollLeft;
        });
        return () => cancelAnimationFrame(raf);
      }
    }
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    sessionStorage.setItem(SCROLL_STORAGE_KEY, String(el.scrollLeft));
  };

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex flex-nowrap gap-3 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pl-3 pr-0 xs:pl-4 min-[430px]:pl-5 min-[520px]:pl-6 sm:pl-7 py-1 scrollbar-hide lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:overflow-visible lg:snap-none lg:px-3 lg:py-1"
      style={{ WebkitOverflowScrolling: "touch", scrollSnapType: "x mandatory" }}
      role="region"
      aria-label="Жизнь церкви"
    >
      {QUICK_ACTIONS.map((action) => (
        <QuickActionCell key={action.id} action={action} />
      ))}
    </div>
  );
}
